import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { BenefitDTOBenefitEnum, BulkCreateBenefitActivationsDTOEmployeeFilter, BulkResultItemDTO } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useTenantID } from 'probonio-shared-ui/module/me';
import { RowSelectionState } from '../../../component/table/useManualRowSelection';
import { useRefetchEmployees } from '../../userManagement/query';
import { EmployeeFilter, mapEmployeeFilterToRequest } from '../employeeStatusList/employeeFilterState';
import { useRefetchActivations } from '../query';

export class BulkPauseFailedError extends Error {
  public readonly results: BulkResultItemDTO[];

  private constructor(results: BulkResultItemDTO[]) {
    super('Bulk pause failed');
    this.results = results;
  }

  public static fromResponse(data: unknown): BulkPauseFailedError {
    const raw = data instanceof Object && 'data' in data ? data.data : undefined;
    const items: BulkResultItemDTO[] = Array.isArray(raw)
      ? raw.filter((item): item is BulkResultItemDTO => item instanceof Object && 'status' in item && typeof item.status === 'string')
      : [];
    return new BulkPauseFailedError(items);
  }
}
export type PauseMutationDTO = {
  endCurrentActivation: string;
  startNewActivation: string;
};

export function useBulkPauseActivationsMutation(
  filter: EmployeeFilter,
  rowSelectionState: RowSelectionState,
  benefit: BenefitDTOBenefitEnum,
  customBenefitId?: string,
) {
  const getTenantId = useTenantID();
  const withMessage = useWithMessage();
  const refetchEmployees = useRefetchEmployees();
  const refetchActivations = useRefetchActivations();

  return useMutation({
    mutationFn: async (bulkPause: PauseMutationDTO) => {
      let employeeFilter: BulkCreateBenefitActivationsDTOEmployeeFilter | undefined, employeeIds;
      if (rowSelectionState.allSelected) {
        employeeFilter = {
          ...mapEmployeeFilterToRequest(filter),
          considerFutureActivations: true,
          activeBenefit: benefit,
          activeCustomBenefitId: customBenefitId,
          excludeEmployeeStatus: ['INACTIVE'],
        };
      } else {
        employeeIds = Object.keys(rowSelectionState.rowSelection).filter(id => rowSelectionState.rowSelection[id]);
      }

      try {
        return await apis.benefits
          .pauseBenefits({
            tenantId: getTenantId(),
            pauseBenefitActivationsDTO: {
              benefit: benefit,
              employeeFilter,
              customBenefitId,
              employeeIds: employeeIds,
              endCurrentActivation: bulkPause.endCurrentActivation,
              startNewActivation: bulkPause.startNewActivation,
            },
          })
          .then(res => res.data);
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 422) {
          throw BulkPauseFailedError.fromResponse(err.response.data);
        }
        throw err;
      }
    },

    onError: err => {
      if (!(err instanceof BulkPauseFailedError)) {
        withMessage(err as Error);
      }
    },
    onSuccess: async () => {
      await refetchEmployees();
      await refetchActivations();
    },
  });
}
